import React, { useState, useEffect } from 'react';
import { MaintenanceContract, MaintenanceCompany, Property } from '../../types';
import { api } from '../../services/api';
import { FileText, Calendar, Plus, AlertTriangle, CheckCircle, Upload, Paperclip, Eye, Loader2, Trash2, Download } from 'lucide-react';

const openBlob = (dataUrl: string, fileName: string, action: 'view' | 'download') => {
    try {
        const arr = dataUrl.split(',');
        if (arr.length < 2) {
            if (dataUrl.startsWith('http') || dataUrl.startsWith('blob:')) {
                if (action === 'view') {
                    window.open(dataUrl, '_blank');
                } else {
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                return;
            }
            throw new Error("تنسيق الملف غير صالح");
        }
        const mime = arr[0].match(/:(.*?);/)?.[1] || '';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);
        if (action === 'view') {
            window.open(url, '_blank');
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
        alert("❌ تعذر معالجة الملف: " + e.message);
    }
};

const ContractDetailsModal = ({ contract, companies, properties, onClose }: { contract: MaintenanceContract, companies: MaintenanceCompany[], properties: Property[], onClose: () => void }) => {
    const [attachments, setAttachments] = useState<any[]>([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

    useEffect(() => {
        if (contract.id) {
            setIsLoadingAttachments(true);
            api.attachments.getByEntity('maintenance_contract', String(contract.id))
                .then(data => setAttachments(data))
                .catch(err => console.error("Error loading attachments", err))
                .finally(() => setIsLoadingAttachments(false));
        }
    }, [contract]);

    const handleViewAttachment = async (id: string, name: string) => {
        try {
            const res = await api.attachments.getData(id);
            if (res && res.file_data) {
                openBlob(res.file_data, name, 'view');
            } else {
                alert("❌ الملف فارغ أو غير موجود");
            }
        } catch (err: any) {
            alert("❌ تعذر عرض المرفق: " + err.message);
        }
    };

    const handleDownloadAttachment = async (id: string, name: string) => {
        try {
            const res = await api.attachments.getData(id);
            if (res && res.file_data) {
                openBlob(res.file_data, name, 'download');
            } else {
                alert("❌ الملف فارغ أو غير موجود");
            }
        } catch (err: any) {
            alert("❌ تعذر تحميل المرفق: " + err.message);
        }
    };

    const companyName = contract.companyName || companies.find(c => String(c.id) === String(contract.companyId))?.name || 'شركة غير معروفة';
    const propertyName = contract.propertyName || properties.find(p => String(p.id) === String(contract.propertyId))?.name || 'عقد شامل لكل العقارات';

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
            <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scaleIn">
                {/* Header */}
                <div className="bg-slate-50 p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                            <FileText className="text-fly-600 w-5 h-5" />
                            <span>تفاصيل عقد الصيانة</span>
                        </h2>
                        <p className="text-[10px] text-slate-400 mt-1">عرض السجل الكامل لعقد الصيانة والمرفقات التابعة له</p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">✕</button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50/50">
                    {/* General Info */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">الشركة المتعاقدة</div>
                                <div className="text-sm font-bold text-slate-800 mt-0.5">{companyName}</div>
                            </div>
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">العقار المشمول</div>
                                <div className="text-sm font-bold text-slate-800 mt-0.5">{propertyName}</div>
                            </div>
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">تاريخ بداية العقد</div>
                                <div className="text-sm font-bold text-slate-800 mt-0.5" dir="ltr">{contract.startDate ? new Date(contract.startDate).toLocaleDateString('ar-KW') : '-'}</div>
                            </div>
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">تاريخ نهاية العقد</div>
                                <div className="text-sm font-bold text-slate-800 mt-0.5" dir="ltr">{contract.endDate ? new Date(contract.endDate).toLocaleDateString('ar-KW') : '-'}</div>
                            </div>
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">قيمة العقد</div>
                                <div className="text-sm font-black text-slate-800 mt-0.5">{contract.contractValue} د.ك ({contract.paymentTerms === 'Monthly' ? 'شهري' : contract.paymentTerms === 'Quarterly' ? 'ربع سنوي' : contract.paymentTerms === 'Yearly' ? 'سنوي' : 'دفعة واحدة'})</div>
                            </div>
                            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                <div className="text-[10px] font-bold text-slate-400">حالة العقد</div>
                                <div className="text-sm font-bold mt-0.5">
                                    {contract.status === 'Active' ? (
                                        <span className="text-green-600 font-bold">ساري</span>
                                    ) : contract.status === 'Expired' ? (
                                        <span className="text-red-600 font-bold">منتهي</span>
                                    ) : (
                                        <span className="text-slate-500 font-bold">ملغي</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                            <div className="text-[10px] font-bold text-slate-400">الوصف والخدمات المشمولة</div>
                            <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{contract.description || 'لا يوجد وصف مضاف'}</div>
                        </div>
                    </div>

                    {/* Attachments */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2 pb-4 border-b border-slate-100">
                            <Paperclip size={18} className="text-slate-600" />
                            <span>مرفقات ومستندات العقد</span>
                        </h3>

                        {isLoadingAttachments ? (
                            <div className="flex items-center justify-center py-6 text-slate-400 gap-2">
                                <Loader2 className="animate-spin text-fly-600" size={16} />
                                <span className="text-xs">جاري تحميل المرفقات...</span>
                            </div>
                        ) : attachments.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {attachments.map((att) => (
                                    <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
                                        <div className="flex items-center gap-3 truncate">
                                            <div className="bg-white p-2 rounded-lg border border-slate-200 text-slate-400 shrink-0">
                                                <FileText size={16} />
                                            </div>
                                            <div className="truncate">
                                                <div className="text-xs font-bold text-slate-800 truncate" title={att.file_name}>{att.file_name}</div>
                                                <div className="text-[9px] text-slate-400 mt-0.5">{new Date(att.uploaded_at || att.uploadedAt || Date.now()).toLocaleDateString('en-GB')}</div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-1.5 shrink-0">
                                            <button
                                                onClick={() => handleViewAttachment(att.id, att.file_name)}
                                                className="p-2 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                                title="معاينة"
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleDownloadAttachment(att.id, att.file_name)}
                                                className="p-2 bg-white text-fly-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                                title="تحميل"
                                            >
                                                <Download size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-xs">
                                لا توجد أي صور أو مرفقات مرفوعة لهذا العقد.
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-slate-200 flex justify-center shrink-0">
                    <button onClick={onClose} className="px-12 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all text-sm">
                        إغلاق
                    </button>
                </div>
            </div>
        </div>
    );
};

const MaintenanceContracts: React.FC = () => {
    const [contracts, setContracts] = useState<MaintenanceContract[]>([]);
    const [companies, setCompanies] = useState<MaintenanceCompany[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingContract, setEditingContract] = useState<MaintenanceContract | null>(null);
    const [isRenewal, setIsRenewal] = useState(false);
    
    const [attachments, setAttachments] = useState<any[]>([]);
    const [pendingAttachments, setPendingAttachments] = useState<{name: string, type: string, data: string}[]>([]);
    const [selectedContractForDetails, setSelectedContractForDetails] = useState<MaintenanceContract | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [payNow, setPayNow] = useState(false);

    // Form State
    const [formData, setFormData] = useState<Partial<MaintenanceContract>>({
        companyId: '',
        propertyId: '',
        startDate: '',
        endDate: '',
        contractValue: 0,
        paymentTerms: 'Monthly',
        description: '',
        status: 'Active'
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [contractsData, companiesData, propertiesData] = await Promise.all([
                api.maintenance.contracts.getAll(),
                api.maintenance.companies.getAll(),
                api.properties.getAll()
            ]);
            setContracts(contractsData);
            setCompanies(companiesData);
            setProperties(propertiesData);
        } catch (error) {
            console.error('Failed to load data', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (editingContract?.id) {
            api.attachments.getByEntity('maintenance_contract', String(editingContract.id))
                .then(data => setAttachments(data))
                .catch(err => console.error("Error loading attachments", err));
        } else {
            setAttachments([]);
        }
    }, [editingContract]);

    const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Check file count limit
        const totalAttachments = (attachments?.length || 0) + (pendingAttachments?.length || 0);
        if (totalAttachments >= 5) {
            alert("⚠️ لا يمكن إضافة المزيد من المرفقات. الحد الأقصى هو 5 ملفات.");
            return;
        }

        // Check file size
        if (file.size > 5 * 1024 * 1024) {
            alert("⚠️ حجم الملف كبير جداً! يجب ألا يتجاوز حجم المرفق 5 ميجابايت.");
            return;
        }

        // Check file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            alert("⚠️ صيغة الملف غير مدعومة. يرجى رفع صور (JPG, PNG, WEBP) أو ملفات PDF فقط.");
            return;
        }
        
        setIsUploading(true);
        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                if (editingContract?.id) {
                    const uploaded = await api.attachments.upload({
                        entity_type: 'maintenance_contract',
                        entity_id: String(editingContract.id),
                        file_name: file.name,
                        file_type: file.type,
                        file_data: reader.result as string
                    });
                    setAttachments(prev => [...prev, uploaded]);
                    alert("✅ تم رفع المرفق بنجاح");
                } else {
                    setPendingAttachments(prev => [...prev, {
                        name: file.name,
                        type: file.type,
                        data: reader.result as string
                    }]);
                    alert("✅ تم إضافة المرفق مؤقتاً وسيجري حفظه مع العقد");
                }
            } catch (error: any) {
                alert("❌ فشل رفع المرفق: " + error.message);
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleDeleteAttachment = async (id: string, isPending: boolean = false, index?: number) => {
        if (!await window.confirm("هل أنت متأكد من حذف هذا المرفق؟")) return;
        try {
            if (isPending && index !== undefined) {
                setPendingAttachments(prev => prev.filter((_, idx) => idx !== index));
                alert("✅ تم حذف المرفق بنجاح");
            } else {
                await api.attachments.delete(id);
                setAttachments(prev => prev.filter(att => String(att.id) !== String(id)));
                alert("✅ تم حذف المرفق بنجاح");
            }
        } catch (error: any) {
            alert("❌ فشل حذف المرفق: " + error.message);
        }
    };

    const handleViewAttachment = async (id: string, name: string) => {
        try {
            const res = await api.attachments.getData(id);
            if (res && res.file_data) {
                openBlob(res.file_data, name, 'view');
            } else {
                alert("❌ الملف فارغ أو غير موجود");
            }
        } catch (err: any) {
            alert("❌ تعذر عرض المرفق: " + err.message);
        }
    };

    const handleOpenEdit = (contract: MaintenanceContract) => {
        setEditingContract(contract);
        setFormData({ 
            ...contract,
            startDate: contract.startDate ? new Date(contract.startDate).toISOString().split('T')[0] : '',
            endDate: contract.endDate ? new Date(contract.endDate).toISOString().split('T')[0] : '',
            propertyId: contract.propertyId || '',
            description: contract.description || '',
            companyId: contract.companyId || '',
            paymentTerms: contract.paymentTerms || 'Monthly',
            status: contract.status || 'Active'
        });
        setIsRenewal(false);
        setPayNow(false);
        setShowForm(true);
    };

    const handleOpenRenew = (contract: MaintenanceContract) => {
        setEditingContract(contract);
        // Default to a 1 year renewal from current end date or today
        let start = new Date(contract.endDate);
        if (isNaN(start.getTime()) || start < new Date()) start = new Date();
        
        const end = new Date(start);
        end.setFullYear(end.getFullYear() + 1);

        setFormData({
            ...contract,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0],
            status: 'Active',
            propertyId: contract.propertyId || ''
        });
        setIsRenewal(true);
        setPayNow(false);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingContract(null);
        setIsRenewal(false);
        setPayNow(false);
        setPendingAttachments([]);
        setAttachments([]);
        setFormData({
            companyId: '', propertyId: '', startDate: '', endDate: '',
            contractValue: 0, paymentTerms: 'Monthly', description: '', status: 'Active'
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const submitData = { ...formData, payNow };
            if (isRenewal) {
                await api.maintenance.contracts.update(editingContract!.id, submitData as any);
                alert('تم تجديد العقد بنجاح');
            } else if (editingContract) {
                await api.maintenance.contracts.update(editingContract.id, submitData as any);
                alert('تم تحديث العقد بنجاح');
            } else {
                const newContract = await api.maintenance.contracts.create(submitData as any);
                if (pendingAttachments.length > 0) {
                    await Promise.all(pendingAttachments.map(att => 
                        api.attachments.upload({
                            entity_type: 'maintenance_contract',
                            entity_id: String(newContract.id),
                            file_name: att.name,
                            file_type: att.type,
                            file_data: att.data
                        })
                    ));
                }
                alert('تم إضافة العقد بنجاح');
            }
            handleCloseForm();
            loadData();
        } catch (error) {
            alert('فشل حفظ بيانات العقد');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.confirm('هل أنت متأكد من رغبتك في حذف هذا العقد؟')) return;
        try {
            await api.maintenance.contracts.delete(id);
            loadData();
            alert('تم حذف العقد بنجاح');
        } catch (error) {
            alert('فشل حذف العقد');
        }
    };

    const isExpiringSoon = (dateStr: string) => {
        if (!dateStr) return false;
        const today = new Date();
        today.setHours(0,0,0,0);
        const endDate = new Date(dateStr);
        if (isNaN(endDate.getTime())) return false;
        const diffTime = endDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 30;
    };

    const isExpired = (dateStr: string) => {
        if (!dateStr) return false;
        const today = new Date();
        today.setHours(0,0,0,0);
        const endDate = new Date(dateStr);
        if (isNaN(endDate.getTime())) return false;
        return endDate < today;
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('ar-KW');
    };

    const inputClass = "block w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm";

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                 <h2 className="text-xl font-bold text-slate-800">عقود الصيانة</h2>
                <button 
                    onClick={() => setShowForm(true)}
                    className="bg-fly-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-fly-700 transition-all font-bold shadow-lg shadow-fly-600/20"
                >
                    <Plus size={18} />
                    إضافة عقد جديد
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-10"><div className="w-8 h-8 border-4 border-fly-200 border-t-fly-600 rounded-full animate-spin"></div></div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {contracts.map(contract => {
                        const expiring = isExpiringSoon(contract.endDate);
                        const expired = isExpired(contract.endDate);
                        
                        return (
                            <div key={contract.id} className={`bg-white p-5 rounded-2xl border ${expiring ? 'border-amber-200 bg-amber-50' : expired ? 'border-red-200 bg-red-50' : 'border-slate-100'} shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <FileText className="text-fly-600" size={20} />
                                        <h3 className="font-bold text-slate-800 text-lg">{contract.companyName}</h3>
                                        {contract.propertyName ? (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-bold">{contract.propertyName}</span>
                                        ) : (
                                            <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-bold">عقد شامل</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-600 mb-3 font-medium">{contract.description || 'لا يوجد وصف مضاف للعقد'}</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                                        <div className="flex items-center gap-2 text-slate-700">
                                            <span className="font-bold text-slate-400">القيمة:</span>
                                            <span className="font-black text-slate-900">{contract.contractValue} KWD</span>
                                            <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 uppercase">{contract.paymentTerms}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-slate-700">
                                            <Calendar size={14} className="text-slate-400" />
                                            <span className="font-bold text-slate-400">الفترة:</span>
                                            <span dir="ltr">{formatDate(contract.startDate)}</span>
                                            <span className="text-slate-300">→</span>
                                            <span dir="ltr" className={expired ? 'text-red-600 font-bold' : expiring ? 'text-amber-600 font-bold' : ''}>{formatDate(contract.endDate)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto shrink-0">
                                    <div className="mb-2 hidden md:block">
                                        {expired ? (
                                            <div className="flex items-center gap-2 text-red-600 font-bold bg-white px-3 py-1 rounded-lg border border-red-100 justify-center">
                                                <AlertTriangle size={18} /> منتهي
                                            </div>
                                        ) : expiring ? (
                                            <div className="flex items-center gap-2 text-amber-600 font-bold bg-white px-3 py-1 rounded-lg border border-amber-100 justify-center">
                                                <AlertTriangle size={18} /> ينتهي قريباً
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 text-green-600 font-bold bg-green-50 px-3 py-1 rounded-lg border border-green-100 justify-center">
                                                <CheckCircle size={18} /> ساري
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 w-full flex-wrap">
                                        <button onClick={() => setSelectedContractForDetails(contract)} className="px-3 py-1.5 bg-slate-50 text-slate-650 hover:bg-slate-200 rounded-lg text-xs font-bold transition-all border border-slate-200 flex items-center justify-center gap-1">
                                            <Eye size={12} />
                                            <span>تفاصيل</span>
                                        </button>
                                        <button onClick={() => handleOpenRenew(contract)} className="flex-1 bg-fly-50 text-fly-600 hover:bg-fly-600 hover:text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-fly-100">تجديد</button>
                                        <button onClick={() => handleOpenEdit(contract)} className="px-3 py-1.5 bg-slate-50 text-slate-600 hover:bg-slate-200 rounded-lg text-xs font-bold transition-all border border-slate-200">تعديل</button>
                                        <button onClick={() => handleDelete(contract.id)} className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg text-xs font-bold transition-all border border-red-100">حذف</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                     {contracts.length === 0 && (
                        <div className="col-span-full text-center py-10 text-slate-400">
                            لا توجد عقود صيانة مسجلة
                        </div>
                    )}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
                    <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 animate-scaleIn">
                        <div className="bg-white p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                                <div className="bg-fly-50 p-2 rounded-lg">
                                    <FileText className="w-5 h-5 text-fly-600" />
                                </div>
                                <span>{isRenewal ? 'تجديد عقد الصيانة' : editingContract ? 'تعديل عقد الصيانة' : 'إضافة عقد صيانة جديد'}</span>
                            </h2>
                            <button type="button" onClick={handleCloseForm} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">✕</button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar bg-slate-50/50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">الشركة *</label>
                                    <select required className={inputClass}
                                        value={formData.companyId} onChange={e => setFormData({...formData, companyId: e.target.value})}>
                                        <option value="">اختر الشركة</option>
                                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">العقار (اختياري - عقد شامل إذا فارغ)</label>
                                    <select className={inputClass}
                                        value={formData.propertyId} onChange={e => setFormData({...formData, propertyId: e.target.value})}>
                                        <option value="">عقد شامل لكل العقارات</option>
                                        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">تاريخ البداية *</label>
                                    <input required type="date" className={inputClass}
                                        value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">تاريخ النهاية *</label>
                                    <input required type="date" className={inputClass}
                                        value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
                                </div>

                                 <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">قيمة العقد *</label>
                                    <input required type="number" className={inputClass}
                                        value={formData.contractValue} onChange={e => setFormData({...formData, contractValue: Number(e.target.value)})} />
                                </div>
                                 <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">طريقة الدفع *</label>
                                    <select className={inputClass}
                                        value={formData.paymentTerms} onChange={e => setFormData({...formData, paymentTerms: e.target.value})}>
                                        <option value="Monthly">شهري</option>
                                        <option value="Quarterly">ربع سنوي</option>
                                        <option value="Yearly">سنوي</option>
                                        <option value="One-time">دفعة واحدة</option>
                                    </select>
                                </div>
                                 <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">الحالة *</label>
                                    <select className={inputClass}
                                        value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as any})}>
                                        <option value="Active">ساري</option>
                                        <option value="Expired">منتهي</option>
                                        <option value="Cancelled">ملغي</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="bg-fly-50 border border-fly-100 p-4 rounded-xl flex items-start gap-3 mt-6">
                                <input 
                                    type="checkbox" 
                                    id="payNow"
                                    checked={payNow}
                                    onChange={(e) => setPayNow(e.target.checked)}
                                    className="mt-1 w-5 h-5 rounded text-fly-600 focus:ring-fly-500"
                                />
                                <div>
                                    <label htmlFor="payNow" className="font-bold text-fly-900 cursor-pointer block">
                                        تسجيل قيمة العقد كمصروف مدفوع من الخزينة الآن؟
                                    </label>
                                    <p className="text-xs text-fly-600 mt-1">
                                        عند تفعيل هذا الخيار، سيتم تسجيل مصروف بقيمة {formData.contractValue || 0} د.ك في الخزينة فوراً.
                                    </p>
                                </div>
                            </div>
                            
                            <div className="mt-6">
                                 <label className="block text-sm font-bold text-slate-700 mb-1.5">وصف العقد / الخدمات المشمولة</label>
                                 <textarea className={inputClass} rows={3}
                                    value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
                            </div>
                            
                            {/* قسم مرفقات عقد الصيانة */}
                            <div className="border-t pt-4 space-y-4">
                                <div className="flex justify-between items-center">
                                    <h4 className="font-bold text-slate-800 flex items-center gap-2">
                                        <Paperclip size={18} className="text-slate-500" />
                                        <span>مرفقات العقد (صور / مستندات)</span>
                                    </h4>
                                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold cursor-pointer hover:bg-slate-800 transition-colors">
                                        {isUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                        <span>رفع مستند</span>
                                        <input 
                                            type="file" 
                                            accept="image/*,application/pdf" 
                                            className="hidden" 
                                            onChange={handleUploadAttachment}
                                            disabled={isUploading}
                                        />
                                    </label>
                                </div>
                                
                                {(attachments.length > 0 || pendingAttachments.length > 0) ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {attachments.map(att => (
                                            <div key={att.id} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                                                <span className="text-xs text-slate-700 truncate font-semibold pl-2" title={att.file_name}>
                                                    {att.file_name}
                                                </span>
                                                <div className="flex gap-1 shrink-0">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleViewAttachment(att.id, att.file_name)}
                                                        className="p-1.5 bg-white text-slate-650 hover:bg-slate-100 rounded-lg border"
                                                        title="عرض"
                                                    >
                                                        <Eye size={12} />
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleDeleteAttachment(att.id, false)}
                                                        className="p-1.5 bg-white text-red-500 hover:bg-red-50 rounded-lg border"
                                                        title="حذف"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        {pendingAttachments.map((att, idx) => (
                                            <div key={`pending-${idx}`} className="flex items-center justify-between p-2.5 bg-blue-50/50 border border-blue-150 rounded-xl">
                                                <span className="text-xs text-blue-800 truncate font-semibold pl-2" title={att.name}>
                                                    {att.name} (معلق)
                                                </span>
                                                <div className="flex gap-1 shrink-0">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => openBlob(att.data, att.name, 'view')}
                                                        className="p-1.5 bg-white text-slate-650 hover:bg-slate-100 rounded-lg border"
                                                        title="عرض"
                                                    >
                                                        <Eye size={12} />
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleDeleteAttachment('', true, idx)}
                                                        className="p-1.5 bg-white text-red-500 hover:bg-red-50 rounded-lg border"
                                                        title="حذف"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4 bg-slate-50 rounded-xl border border-dashed text-slate-400 text-xs">
                                        لا توجد مرفقات مرفوعة حالياً. يمكنك رفع ملفات الشروط أو صور الفواتير والضمان.
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="p-4 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0">
                            <button type="button" onClick={handleCloseForm} className="px-6 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-xs font-bold font-bold">إلغاء</button>
                            <button type="submit" className="px-8 py-2.5 bg-fly-600 text-white rounded-xl font-bold hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-xs font-bold font-bold">
                                {isRenewal ? 'تجديد العقد' : editingContract ? 'تحديث العقد' : 'حفظ العقد'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {selectedContractForDetails && (
                <ContractDetailsModal 
                    contract={selectedContractForDetails}
                    companies={companies}
                    properties={properties}
                    onClose={() => setSelectedContractForDetails(null)}
                />
            )}
        </div>
    );
};

export default MaintenanceContracts;
